import type { MultiConnectionGroupItem, SingleConnectionGroupItem } from "@wayflyer/flyui";
import { Image } from "@wayflyer/flyui";
import type { ConnectorPropertiesSchema } from "shared-ui";
import { IconShop24Line } from "@wayflyer/flyui-icons/24/line";
import type { IntlShape } from "react-intl";

import { COMMON_MESSAGES } from "../components/common-messages";

import { NOT_LISTED } from "./funding-journey";

export type SelectedAdditionalPlatform = { platform: string; category: string; isSupported: boolean };

const labelMap: Record<string, string> = {
  quickbooks: "QuickBooks",
};

export const getChecklistItemPropsForConnector = (
  connector: ConnectorPropertiesSchema,
  checked: boolean,
  onPlatformChange: ({ slug, value }: { slug: string; value: boolean }) => void,
  readonly = false,
  disabled = false,
  hasError = false
): MultiConnectionGroupItem => {
  const logo = connector.connectionLogoSrc ? (
    <Image key={connector.slug} src={connector.connectionLogoSrc} />
  ) : (
    <IconShop24Line />
  );

  return {
    checked,
    label: labelMap[connector.slug] || connector.name,
    logo,
    readonly,
    disabled,
    hasError,
    onChange: (selected: boolean) => onPlatformChange({ slug: connector.slug, value: selected }),
    name: connector.slug,
    connectionCount: connector.connections.length,
  };
};

export const getChecklistItemForUnsupportedPlatform = (
  platform: string,
  category: string,
  checked: boolean,
  onPlatformChange: ({ category, name, value }: { category: string; name: string; value: boolean }) => void,
  intl: IntlShape
): MultiConnectionGroupItem =>
  platform === NOT_LISTED
    ? {
        checked,
        label: intl.formatMessage(COMMON_MESSAGES.iDontSeeMyPlatformListed),
        logo: <div style={{ width: "16px" }} />,
        onChange: (selected: boolean) => onPlatformChange({ category, name: platform, value: selected }),
        name: "add-unsupported-platform",
      }
    : {
        checked,
        label: platform,
        logo: <IconShop24Line />,
        onChange: (selected: boolean) => onPlatformChange({ category, name: platform, value: selected }),
        name: platform,
      };

export const getRadioChecklistItemPropsForConnector = (
  connector: ConnectorPropertiesSchema,
  checked: boolean,
  onPlatformChange: ({ platform, category, isSupported }: SelectedAdditionalPlatform) => void
): SingleConnectionGroupItem => {
  const logo = connector.connectionLogoSrc ? (
    <Image key={connector.slug} src={connector.connectionLogoSrc} />
  ) : (
    <IconShop24Line />
  );

  return {
    checked,
    name: "add-platform",
    value: connector.slug,
    label: labelMap[connector.slug] || connector.name,
    logo,
    onChange: () => onPlatformChange({ platform: connector.slug, category: connector.category, isSupported: true }),
  };
};

export const getRadioChecklistItemPropsForUnsupportedPlatform = (
  platform: string,
  category: string,
  checked: boolean,
  onPlatformChange: ({ platform, category, isSupported }: SelectedAdditionalPlatform) => void
): SingleConnectionGroupItem => ({
  checked,
  name: "add-platform",
  value: platform,
  label: platform,
  logo: <IconShop24Line />,
  onChange: () => onPlatformChange({ platform, category, isSupported: true }),
});
